<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import {
  focusableIn,
  isTopModal,
  lockBodyScroll,
  pushModal,
  removeModal,
  trapTabKey,
} from "@/components/overlay";
import { provideReasonContext } from "@/components/reasonContext";
import ReasonScope from "@/components/ReasonScope.vue";
import RoleLoweredStrip from "@/components/RoleLoweredStrip.vue";
import UiIcon from "@/components/UiIcon.vue";
import { focusAfterSwap } from "@/utils/scroll";

/**
 * 표준 모달(ds-audit C3)
 * - 마운트되면 열리고 언마운트되면 닫힌다. 부모가 `v-if` 로 띄우고 `@close` 에서 닫을지 정한다(저장 중이면 무시하는 식).
 * - `#app` 안으로 Teleport 한다 — body 로 보내면 `#app#app` 아래 표준 부품(ui-callout · ui-status · 컨트롤 크기)이 모달 안에서 빠진다.
 * - 접근성: role=dialog · aria-modal · aria-labelledby(제목), ESC 닫기(맨 위 모달만), Tab 순환, 열릴 때 첫 포커스,
 *   닫힐 때 연 요소로 포커스 복귀, body 스크롤 잠금(겹친 모달은 개수로 센다).
 * - 모양은 main.scss `.app-modal.ui-modal`: 머리(제목 · 닫기) / 본문(스크롤) / 바닥(버튼 줄 고정) 3단.
 * - 바닥 버튼 순서는 [보조(ghost) … 주(primary)] — 주 동작이 맨 오른쪽이다.
 * - 93차 C93-13(UXS93-01): 바닥 줄 안 `LoadingButton` 의 막힘 이유(`blockedReason`)는 자리를 호출마다 고르지 않는다 — `group` 으로 풀려 버튼은 감싸개 없이 [취소 | 주] 직계로 남고,
 *   이유 글은 이 부품이 바닥 줄 바로 위에 `p.app-modal-reason.ui-btn-reason.ui-btn-group-reason` 한 줄로 그린다(이유가 있을 때만 · 같은 글은 한 줄). 호출부가 따로 쓰지 않는다.
 *   명시 `reasonPlacement` 를 준 버튼과 본문 안 버튼은 그대로(본문은 아래 `below`).
 * - 부모가 준 class · 속성은 패널(`.app-modal`)에 붙는다. 부모 scoped 스타일은 슬롯 내용에는 그대로 먹지만 패널 자체에는 먹지 않으므로
 *   패널 폭은 `size` 로 고른다.
 * - 닫기(×)는 확인창(`sm`)만 기본으로 없고 입력형 · 넓은 · 전체 모달은 기본으로 둔다(UXE-14). 끄려면 `:closable="false"`.
 *   × 는 자동이든 명시든 `busy` 일 때만 잠긴다 — 처리 중 잠금은 `closeOnEsc` · `closeOnBackdrop` 토글이 아니라 `busy` 로 준다
 *   (개발 모드에서 × 가 보이는데 `closeOnEsc` 만 끄면 경고한다).
 * - 90차 C90-21(JR90-10): 본문(`.app-modal-body`) 안에서 포커스가 가면 그 요소가 본문 상자 위 · 아래로 걸쳐 잘렸을 때만 `scrollIntoView({ block: "nearest" })`(포커스 링이 들어갈 6px 여유 — 바닥 버튼 줄에 마지막 칸 아래가 가려지던 것).
 *   다 보이는 칸은 움직이지 않는다. 포커스 자체는 바꾸지 않는다.
 * - 93차 C93-12(JR93-02): 저장 중(`busy`)에 잠긴 입력(`:disabled`)이 포커스를 들고 있으면 포커스가 body 로 빠지고, 실패해 `busy` 가 풀려도 그대로 body 에 남았다(Enter 제출 모달).
 *   패널 안 마지막 포커스를 기억해 두었다가 `busy` 가 true → false 가 되는 순간 포커스가 body · 패널 밖이면 그 요소(연결 · 사용 가능)로, 없으면 첫 `aria-invalid` 칸, 그것도 없으면 패널로 되돌린다.
 *   잃지 않았으면 아무것도 하지 않고, 성공해 모달이 닫히는 경로는 닫힘 복귀가 처리한다(이 감시는 모달이 사라지면 함께 멈춘다). 화면은 입력 잠금을 `readonly` + `aria-disabled` 로 바꾸는 쪽이 정본이다.
 * - `size="full"` 은 1200 × 화면 높이 90% 고정이고 본문이 세로 flex 다 — 마지막 자식이 남은 높이를 채우므로 그 안에 2열을 두고
 *   열마다 `overflow-y: auto` 로 따로 스크롤한다(이력서 일괄 등록 목록 | 상세).
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** 제목. `header` 슬롯을 쓰면 슬롯의 제목 요소에 슬롯 인자 `titleId` 를 id 로 붙인다. */
    title?: string;
    /** 제목 위 파란 알약 라벨(`header` 슬롯에 `.kicker` 를 직접 그리던 것을 대신한다) */
    kicker?: string;
    /**
     * 저장 · 생성처럼 되돌릴 수 없는 처리 중. true 면 ESC · 배경막 · × 닫기 요청을 보내지 않고 패널에 `aria-busy` 를 둔다
     * (`closeOnEsc` · `closeOnBackdrop` 을 저장 상태로 토글하던 것을 대신한다).
     */
    busy?: boolean;
    /** 폭: 확인형 440 · 입력형 640 · 넓은 960 · 전체 1200(높이 90dvh 고정) */
    size?: "sm" | "form" | "wide" | "full";
    /** 머리 오른쪽에 닫기(×) 버튼을 둔다. 주지 않으면 `size !== "sm"` */
    closable?: boolean;
    /** ESC 로 닫기 요청(`close`)을 보낸다. 처리 중에 막으려면 이 값을 토글하지 말고 `busy` 를 쓴다. */
    closeOnEsc?: boolean;
    /** 배경막 클릭으로 닫기 요청을 보낸다 */
    closeOnBackdrop?: boolean;
    /** 열릴 때 포커스할 요소(패널 안 CSS 선택자). 없으면 본문의 첫 입력칸, 그것도 없으면 패널 자체. */
    initialFocus?: string;
    /** 본문을 aria-describedby 로 잇는다(짧은 확인 문구 모달용) */
    describeBody?: boolean;
    /** `aria-describedby` 가 가리킬 요소 id — 본문 전체가 아니라 한 문장만 읽혀야 할 때(`ConfirmDialog` — 접힘 「영향 자세히」 가 열릴 때 낭독되지 않게). `describeBody` 보다 우선 */
    describedBy?: string;
  }>(),
  {
    title: undefined,
    kicker: undefined,
    busy: false,
    size: "sm",
    // undefined 를 기본값으로 둬야 Vue 가 빠진 boolean prop 을 false 로 바꾸지 않는다(아래 showClose 가 크기로 정한다).
    closable: undefined,
    closeOnEsc: true,
    closeOnBackdrop: true,
    initialFocus: undefined,
    describeBody: false,
    describedBy: undefined,
  },
);

const emit = defineEmits<{
  /** 닫기 요청. 실제로 닫는 것(v-if 해제)은 부모가 한다. */
  close: [reason: "esc" | "backdrop" | "button"];
}>();

defineSlots<{
  /** 제목 줄을 직접 그릴 때 */
  header?: (props: { titleId: string }) => unknown;
  /** 본문(길면 이 영역만 스크롤) */
  default?: () => unknown;
  /** 바닥 버튼 줄 */
  actions?: () => unknown;
}>();

// 78차 UXS78-03: 안쪽 `LoadingButton`(기본 `reasonPlacement="auto"`)의 막힌 이유는 바깥 문맥(히어로 · 섹션 머리 동작 줄)을 상속하지 않고 버튼 아래 한 줄(below)이다.
provideReasonContext({ place: "below" });

// 93차 C93-13(UXS93-01): 바닥 줄(`#actions`) 버튼의 막힘 이유는 버튼 옆 감싸개가 아니라 **줄 위 한 줄**로 모달이 한 번 그린다 — 바닥 줄은 늘 [취소 | 주] 직계 둘이다.
// 같은 글을 나누는 형제는 한 줄(중복 제거). 버튼마다 알리므로 글이 다른 버튼이 둘이면 그 수만큼 줄이 선다.
const reasonById = reactive(new Map<string, string>());
const reasonLines = computed(() => [...new Set(reasonById.values())]);

function collectReason(id: string, text: string | undefined): void {
  if (text) reasonById.set(id, text);
  else reasonById.delete(id);
}

const showClose = computed(() => props.closable ?? props.size !== "sm");

if (import.meta.env.DEV)
  watch(
    () => props.closeOnEsc,
    (onEsc) => {
      if (!onEsc && !props.busy && showClose.value)
        console.warn(
          "[AppModal] closeOnEsc 만 끄면 닫기(×)는 잠기지 않습니다 — 처리 중 잠금은 busy 로 주세요",
        );
    },
    { immediate: true },
  );

const titleId = useId();
const bodyId = useId();
const panel = useTemplateRef<HTMLElement>("panel");

// 열기 직전 포커스(모달을 연 버튼)를 기억해 닫힐 때 되돌린다.
let opener =
  document.activeElement instanceof HTMLElement ? document.activeElement : null;
const modalId = pushModal();
// 패널 안에서 마지막으로 포커스를 받은 요소(C93-12) — 저장 중 잠금으로 포커스를 잃었을 때 돌려준다.
let lastPanelFocus: HTMLElement | null = null;
const unlockScroll = lockBodyScroll();
let pressedOnBackdrop = false;

function onKeydown(event: KeyboardEvent): void {
  if (!isTopModal(modalId) || !panel.value) return;
  // 한글 입력 조합 중 ESC 는 조합 취소라 모달을 닫지 않는다.
  if (event.key === "Escape" && !event.isComposing) {
    // 아래에 깔린 화면 · 모달의 ESC 처리(window 리스너)까지 가지 않게 여기서 멈춘다.
    event.stopPropagation();
    if (props.closeOnEsc && !props.busy) emit("close", "esc");
    return;
  }
  trapTabKey(event, panel.value);
}

// 패널 안에서 누른 채 배경막에서 뗀 드래그(글자 선택)로 닫히지 않게, 누르기와 떼기가 모두 배경막일 때만 닫는다.
function onBackdropPointerdown(event: PointerEvent): void {
  pressedOnBackdrop = event.target === event.currentTarget;
}

function onBackdropClick(event: MouseEvent): void {
  const onBackdrop = pressedOnBackdrop && event.target === event.currentTarget;
  pressedOnBackdrop = false;
  if (onBackdrop && props.closeOnBackdrop && !props.busy) {
    emit("close", "backdrop");
  }
}

/** 포커스 링이 잘리지 않게 두는 여유(px) */
const FOCUS_SCROLL_MARGIN = 6;

// 본문 안 포커스가 본문 상자 위 · 아래로 걸치면 한 번 보이게 한다(JR90-10). 다 보이면 아무것도 하지 않는다.
function onBodyFocusin(event: FocusEvent): void {
  const target = event.target;
  const body = event.currentTarget;
  if (!(target instanceof HTMLElement) || !(body instanceof HTMLElement))
    return;
  const rect = target.getBoundingClientRect();
  const box = body.getBoundingClientRect();
  if (rect.height === 0 || box.height === 0) return;
  if (
    rect.top >= box.top + FOCUS_SCROLL_MARGIN / 2 &&
    rect.bottom <= box.bottom - FOCUS_SCROLL_MARGIN / 2
  )
    return;
  const previous = target.style.scrollMargin;
  target.style.scrollMargin = `${FOCUS_SCROLL_MARGIN}px 0`;
  target.scrollIntoView({ block: "nearest" });
  target.style.scrollMargin = previous;
}

function onPanelFocusin(event: FocusEvent): void {
  const el = event.target;
  if (el instanceof HTMLElement && el !== panel.value) lastPanelFocus = el;
}

// 저장 중 → 끝(실패로 모달이 남음): 포커스를 잃었으면 마지막 요소 → 첫 오류 칸 → 패널 순으로 되돌린다.
watch(
  () => props.busy,
  (busy, wasBusy) => {
    if (busy || !wasBusy) return;
    void nextTick(() => {
      const root = panel.value;
      if (!root || !isTopModal(modalId)) return;
      const active = document.activeElement;
      if (active && active !== document.body && root.contains(active)) return;
      void focusAfterSwap(() => lastPanelFocus, {
        fallback: () =>
          root.querySelector<HTMLElement>('[aria-invalid="true"]') ?? root,
      });
    });
  },
  { flush: "post" },
);

// 처리 중 ×는 `disabled` 가 아니라 `aria-disabled` 로 막는다 — disabled 로 바꾸면 그 버튼에 있던 포커스가 body 로 빠진다.
function onCloseClick(): void {
  if (!props.busy) emit("close", "button");
}

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
  // 아래에 다른 모달이 떠 있는데 연 요소가 그 모달 밖이면(확인창 두 개가 한 틱에 쌓인 경우 둘 다 같은 버튼을 기억한다) 닫힐 때 아래
  // 모달 패널로 포커스를 돌린다 — 원래 버튼은 아래 모달 뒤라 닿을 수 없다(리뷰 B11).
  if (!opener?.closest(".app-modal")) {
    const below = [
      ...document.querySelectorAll<HTMLElement>(".app-modal"),
    ].filter((el) => el !== panel.value);
    opener = below.at(-1) ?? opener;
  }
  void nextTick(() => {
    const root = panel.value;
    if (!root) return;
    const target =
      (props.initialFocus
        ? root.querySelector<HTMLElement>(props.initialFocus)
        : null) ??
      root.querySelector<HTMLElement>("[autofocus]") ??
      focusableIn(root).find((el) =>
        el.matches(".app-modal-body :is(input, select, textarea)"),
      ) ??
      root;
    target.focus({ preventScroll: true });
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  removeModal(modalId);
  unlockScroll();
  if (opener?.isConnected) opener.focus({ preventScroll: true });
});
</script>

<template>
  <Teleport to="#app">
    <div
      class="app-modal-backdrop"
      role="presentation"
      @pointerdown="onBackdropPointerdown"
      @click="onBackdropClick"
    >
      <div
        ref="panel"
        v-bind="$attrs"
        class="app-modal ui-modal"
        :class="size === 'sm' ? undefined : `is-${size}`"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title || $slots.header ? titleId : undefined"
        :aria-describedby="describedBy ?? (describeBody ? bodyId : undefined)"
        :aria-busy="busy || undefined"
        tabindex="-1"
        @focusin="onPanelFocusin"
      >
        <div v-if="title || $slots.header || showClose" class="app-modal-head">
          <slot name="header" :title-id="titleId">
            <div v-if="kicker">
              <span class="kicker app-modal-kicker">{{ kicker }}</span>
              <h2 v-if="title" :id="titleId" class="app-modal-title">
                {{ title }}
              </h2>
            </div>
            <h2 v-else-if="title" :id="titleId" class="app-modal-title">
              {{ title }}
            </h2>
          </slot>
          <button
            v-if="showClose"
            type="button"
            class="app-modal-close"
            aria-label="닫기"
            :aria-disabled="busy || undefined"
            @click="onCloseClick"
          >
            <UiIcon name="close" :size="20" />
          </button>
        </div>
        <RoleLoweredStrip />
        <div :id="bodyId" class="app-modal-body" @focusin="onBodyFocusin">
          <slot />
        </div>
        <!-- 막힘 이유 한 줄(C93-13) — 바닥 줄 바로 위 형제. 이유가 없으면 그리지 않는다. -->
        <p
          v-for="line in reasonLines"
          :key="line"
          class="app-modal-reason ui-btn-reason ui-btn-group-reason"
        >
          {{ line }}
        </p>
        <div v-if="$slots.actions" class="app-modal-actions">
          <ReasonScope place="group" :collect="collectReason">
            <slot name="actions" />
          </ReasonScope>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/*
 * 83차 M16(UXC83-02): `#app` 로 Teleport 한 층은 `.content .page { word-break: keep-all }`(원본 base.scss) 를 못 받아 한글이 단어 한가운데서 꺾였다.
 * 상속이라 슬롯 내용 전부가 받고, 긴 영문 · URL 은 `anywhere` 로 계속 꺾인다.
 */
.app-modal {
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 패널 자체에 포커스가 갈 때(입력칸 없는 모달)는 테두리 링 대신 아무 표시도 두지 않는다 — 제목이 읽히고 Tab 이 첫 버튼으로 간다. */
.app-modal:focus {
  outline: none;
}

/* 제목 위 알약 — 화면 3곳(근거 · 직무기술서 · 충돌 확인)이 따로 주던 아래 여백을 한 곳에 둔다. */
.app-modal-kicker {
  margin: 0 0 var(--ui-gap-sm);
}

/* 전체 모달 — 폭 1200 · 높이 90dvh 고정(행이 늘어도 틀이 출렁이지 않는다). 본문은 세로 flex, 마지막 자식이 남은 높이를 채운다. */
.app-modal.is-full {
  width: min(1200px, 100%);
  height: 90vh;
  height: 90dvh;
}

.ui-modal.is-full > .app-modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--ui-gap-md);
}

.ui-modal.is-full > .app-modal-body > :slotted(:last-child) {
  flex: 1 1 auto;
  min-height: 0;
}

/* 본문이 스크롤되는 큰 모달이라 입력형 · 넓은 모달처럼 바닥 줄을 구분선으로 뗀다(전역 `.ui-modal:is(.is-form, .is-wide)` 와 같은 값). */
.ui-modal.is-full > .app-modal-actions {
  padding-top: 14px;
  border-top: 1px solid var(--ui-line);
}

@media (max-width: 620px) {
  .ui-modal.is-full > .app-modal-actions {
    padding-top: 12px;
  }
}

/* 처리 중 × — 눌러도 닫히지 않음을 보인다. */
.app-modal-close[aria-disabled="true"],
.app-modal-close[aria-disabled="true"]:hover {
  background: transparent;
  color: var(--ui-disabled);
  cursor: not-allowed;
}
</style>
